import { useState, useContext, useEffect, useMemo } from "react"
import { db, timestamp } from "../../firebase/config.js"
import { useFirestoreGeneralOrderBy } from "../../firebase/useFirestore";
import { client } from "../../hooks/Client";
import { useHistory } from "react-router-dom";
import useSettings from "../../hooks/Settings";
import { Auth } from "../../StateManagment/Auth";
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import { SavedIcon } from "../../StateManagment/SavedIcon";
import Tooltip from "../../components/common/Tooltip";
import DragIndicatorOutlinedIcon from '@mui/icons-material/DragIndicatorOutlined';
import TasksModal from "../../components/events/TasksModal.jsx";
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import MeasureMomentTasksCounter from "./MeasureMomentTasksCounter";
import Table from "../common/table/Table.jsx";
import MeasureMomentQuestionsCell from "./MeasureMomentQuestionsCell.jsx";
import TableCell from "../common/table/TableCell.jsx";
import TableDragHandleCell from "../common/table/TableDragHandleCell.jsx";
import { syncQualifyingExclusions } from "../../helpers/questionnaire/applyQualifyingToggle";

/**
 * @param {object} props
 * @param {Array<object>} props.measureMoments - The MeasureMoments docs to render.
 * @param {(moment: object) => void} [props.onSchedule] - Optional: when provided, the planning
 *   icon calls this instead of opening TasksModal here (avoids stacking modals).
 * @param {string} [props.personaID] - Optional persona id, threaded to the tasks counter.
 * @param {object} [props.research] - The parent Research doc; required for the (Pro) import column.
 */
const MeasureMoment = ({measureMoments, onSchedule, personaID = '', research}) => {
    // Context
    const [saved, setSaved] = useContext(SavedIcon)
    const [auth] = useContext(Auth)

    // Hooks
    const history = useHistory()
    const deccosPro = useSettings().deccosPro

    // The dataset-import column is a Deccos Pro feature, only on the research builder
    // (where a `research` is passed and there's no schedule-callback override).
    const showImport = deccosPro && !!research?.ID && !onSchedule

    // Vragen per meetmoment kies je bij het onderzoek zelf, niet in het
    // planningsscherm (waar `onSchedule` de rij-actie overneemt).
    const showQuestions = !onSchedule

    // Eén keer ophalen voor alle rijen: de meetmomenten van een onderzoek delen
    // dezelfde vragenlijst (MeasureMoments.QuestionnaireID spiegelt Research).
    const questionnaireID = showQuestions
        ? (research?.QuestionnaireID || measureMoments?.[0]?.QuestionnaireID || '')
        : ''
    // De hook-engine filtert soft-deleted docs al; alleen de identiteit moet
    // stabiel blijven, anders vervalt elke useMemo in de cel en de modal.
    const questionnaireFieldsRaw = useFirestoreGeneralOrderBy(
        'QuestionnaireFields',
        'QuestionnaireID',
        questionnaireID || '',
        'Position',
        'asc'
    )
    const questionnaireFields = useMemo(
        () => (Array.isArray(questionnaireFieldsRaw) ? questionnaireFieldsRaw : []),
        [questionnaireFieldsRaw]
    )

    // Kwalificeringsvragen staan alleen op het eerste meetmoment. Verslepen en
    // verwijderen bepalen welk moment dat is, dus daarna de uitsluitingen
    // opnieuw uitrekenen — ook in het planningsscherm, waar geen onderzoek
    // wordt meegegeven. Een mislukte herberekening wordt niet afgevangen: de
    // wijziging zelf is dan wél opgeslagen, en de fout hoort zichtbaar te blijven.
    // AI-CONTEXT: See docs/features/sector-kwalificeringsvragen.md
    const momentsQuestionnaireID = research?.QuestionnaireID || measureMoments?.[0]?.QuestionnaireID || ''
    // De velden van de vragenlijst gaan mee als de listener hierboven ze al
    // heeft, zodat de herberekening ze niet opnieuw leest (performancecheck
    // /complete 2026-10-28). In het planningsscherm draait die listener niet;
    // dan leest de herberekening zelf.
    const resyncQualifying = () => syncQualifyingExclusions({
        questionnaireId: momentsQuestionnaireID,
        client,
        fields: Array.isArray(questionnaireFieldsRaw) ? questionnaireFieldsRaw : undefined,
    })

    // State
    const [dragArray, setDragArray] = useState([]);
    const [modalOpen, setModalOpen] = useState(false)
    const [activeMoment, setActiveMoment] = useState(null)

    // Hooks
    // Whenever Firestore data changes, update dragArray state.
    // Soft-deleted moments (ADR-008) stay in Firestore but are hidden here.
    useEffect(() => {
        setDragArray((measureMoments || []).filter((moment) => !moment.Deleted));
    }, [measureMoments]);

    // DRAG & DROP: reorder items and update Firestore
    const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(dragArray);
    const [movedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, movedItem);

    // Update local state to reflect the reorder immediately
    setDragArray(items);

    // Now update Firestore with new positions
    Promise.all(items.map((dragArray, idx) => (
        db.collection('MeasureMoments')
        .doc(dragArray.docid) // assuming .docid is stored for each doc
        .update({ Position: idx + 1 })
    ))).then(resyncQualifying);

    // Show "saved" icon
        setSaved('flex');
    };

    // Edit title
    const titleHandler = (e) => {

        const docid = e.target.dataset.docid
        const title = e.target.value

        db.collection('MeasureMoments')
        .doc(docid)
        .update({
            Title: title
        })
        .then(() => {
            setSaved('flex')
         })

    }

    // Change moment date
    const changeMomentDateHandler = (e) => {
        const date = e.target.value
        const docid = e.target.dataset.docid

        db.collection('MeasureMoments')
        .doc(docid)
        .update({
            Moment: date
        })
        .then(() => {
            setSaved('flex')
         })

    }

    // Delete moment (soft delete, ADR-008)
    const deleteMoment = (e) => {
        // currentTarget, not target: clicks land on the <path> inside the MUI
        // icon, which carries none of the data-attributes.
        const docid = e.currentTarget.dataset.docid

        db.collection('MeasureMoments')
        .doc(docid)
        .update({
            Deleted: true,
            DeletedAt: timestamp,
            DeletedBy: auth?.ID || '',
        })
        .then(resyncQualifying)

    }

    return(
        <Table
            items={dragArray || []}
            columns={[
                { key: 'drag', label: '', searchable: false },
                { key: 'title', label: 'TITEL', searchAccessor: (moment) => moment.Title || '' },
                { key: 'planning', label: 'PLANNING', searchable: false },
                ...(showQuestions ? [{ key: 'questions', label: 'VRAGEN', searchable: false }] : []),
                ...(showImport ? [{ key: 'import', label: 'IMPORTEREN', searchable: false }] : []),
                { key: 'delete', label: 'VERWIJDEREN', searchable: false },
            ]}
            draggable
            droppableId="moments"
            onDragEnd={handleDragEnd}
            showSearch={false}
            emptyMessage="Geen meetmomenten gevonden."
            getItemKey={(moment) => moment.ID}
            renderRow={(moment) => (
                <>
                    <TableDragHandleCell>
                        <DragIndicatorOutlinedIcon />
                    </TableDragHandleCell>
                    <TableCell>
                        <Tooltip content={moment.Title} width='80%' top='-35px'>
                            <input type="text" placeholder="Schrijf hier de titel" data-docid={moment.docid} defaultValue={moment.Title} onChange={titleHandler}/>
                        </Tooltip>
                    </TableCell>
                    <TableCell>
                        <div className="measure-moment-tasks-counter-edit-icon-container">
                            <MeasureMomentTasksCounter measureMomentId={moment.ID} personaID={personaID} />
                            <EditOutlinedIcon onClick={() => {
                                if (onSchedule) {
                                    onSchedule(moment)
                                } else {
                                    setActiveMoment(moment)
                                    setModalOpen(true)
                                }
                            }} />
                        </div>

                        {!onSchedule && activeMoment?.ID === moment.ID &&
                            <TasksModal measureMomentID={moment.ID} setModalOpen={setModalOpen} modalOpen={modalOpen} type='measureMoment'/>
                        }
                    </TableCell>
                    {showQuestions &&
                        <TableCell>
                            <MeasureMomentQuestionsCell
                                moment={moment}
                                fields={questionnaireFields}
                                siblingMoments={dragArray}
                                hasQuestionnaire={!!questionnaireID}
                            />
                        </TableCell>
                    }
                    {showImport &&
                        <TableCell>
                            <Tooltip content={'Dataset importeren'} top='-45px'>
                                <FileUploadOutlinedIcon
                                    className='measure-moment-import-icon'
                                    onClick={() => history.push(`/${client}/importmeasuremoment/${research.ID}/${moment.ID}`)}
                                />
                            </Tooltip>
                        </TableCell>
                    }
                    <TableCell>
                        <DeleteOutlinedIcon className='table-delete-icon' data-docid={moment.docid} onClick={deleteMoment} />
                    </TableCell>
                </>
            )}
        />
    )
}

export default MeasureMoment
